// Convex logEvent is internal (relic of third-party logging); no client calls.

// Global reference to the Convex client - will be set by the app
let convexClient: any = null;
// Lazy-loaded Sentry reference to avoid blocking initial load
let SentryModule: typeof import('@sentry/react') | null = null;

export function setConvexClient(client: any) {
  convexClient = client;
}

// Lazy-load Sentry module to avoid blocking initial render
async function getSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return null;
  if (!SentryModule && typeof window !== 'undefined') {
    try {
      SentryModule = await import('@sentry/react');
    } catch (error) {
      console.warn('Failed to load Sentry:', error);
    }
  }
  return SentryModule;
}

// Unified logging interface that sends to Sentry (frontend) and Convex logs (backend via Convex)
export class Logger {
  private userId?: string;
  private userEmail?: string;
  private isAdmin?: boolean;

  async setUser(userId: string, userEmail?: string, isAdmin?: boolean) {
    this.userId = userId;
    this.userEmail = userEmail;
    this.isAdmin = isAdmin;
    
    // Set user context in Sentry (async to avoid blocking)
    const Sentry = await getSentry();
    if (Sentry) {
      Sentry.setUser({
        id: userId,
        email: userEmail,
        username: userEmail,
      });
    }
  }

  async clearUser() {
    this.userId = undefined;
    this.userEmail = undefined;
    this.isAdmin = undefined;
    
    // Clear user context in Sentry
    const Sentry = await getSentry();
    if (Sentry) {
      Sentry.setUser(null);
    }
  }

  private sendToConvex(level: 'info' | 'warn' | 'error' | 'debug', message: string, metadata?: Record<string, any>) {
    // Convex logEvent is internal; use console as fallback until Seq is implemented
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[${level.toUpperCase()}] ${message}`, metadata);
    }
  }

  info(message: string, metadata?: Record<string, any>) {
    this.sendToConvex('info', message, metadata);
    // Sentry captures info-level logs as breadcrumbs (async to avoid blocking)
    getSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.addBreadcrumb({
          message,
          level: 'info',
          data: metadata,
        });
      }
    });
  }

  warn(message: string, metadata?: Record<string, any>) {
    this.sendToConvex('warn', message, metadata);
    // Sentry captures warnings (async to avoid blocking)
    getSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.captureMessage(message, {
          level: 'warning',
          extra: metadata,
        });
      }
    });
  }

  error(message: string, error?: Error, metadata?: Record<string, any>) {
    // Send to Convex for backend logging (synchronous for critical errors)
    this.sendToConvex('error', message, {
      ...metadata,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : undefined,
    });
    
    // Send to Sentry for frontend error tracking (async to avoid blocking)
    getSentry().then((Sentry) => {
      if (Sentry) {
        if (error) {
          Sentry.captureException(error, {
            tags: {
              customMessage: message,
            },
            extra: metadata,
          });
        } else {
          Sentry.captureMessage(message, {
            level: 'error',
            extra: metadata,
          });
        }
      }
    });
  }

  debug(message: string, metadata?: Record<string, any>) {
    this.sendToConvex('debug', message, metadata);
    // Sentry captures debug-level logs as breadcrumbs (async to avoid blocking)
    getSentry().then((Sentry) => {
      if (Sentry) {
        Sentry.addBreadcrumb({
          message,
          level: 'debug',
          data: metadata,
        });
      }
    });
  }

  // Mortgage-specific methods
  calculationStarted(formData: any) {
    this.info('Mortgage calculation started', {
      loanType: formData.property?.loanType,
      propertyValue: formData.property?.propertyValue,
      loanRequired: formData.property?.loanRequired,
      totalIncome: formData.income?.totalIncomeNetOfTax,
    });
  }

  calculationCompleted(results: any) {
    this.info('Mortgage calculation completed', {
      maxLoan5Year: results.maxLoan5YearFixed,
      maxLoanOther: results.maxLoanOther,
      calculationTime: Date.now(),
    });
  }

  calculationSaved(resultId: string) {
    this.info('Calculation result saved', { resultId });
  }

  calculationLoaded(resultId: string) {
    this.info('Calculation result loaded', { resultId });
  }

  calculationDeleted(resultId: string) {
    this.info('Calculation result deleted', { resultId });
  }

  userSignIn() {
    this.info('User signed in', {
      userId: this.userId,
      userEmail: this.userEmail,
      isAdmin: this.isAdmin,
    });
  }

  userSignOut() {
    this.info('User signed out', {
      userId: this.userId,
      userEmail: this.userEmail,
    });
  }

  formReset() {
    this.info('Form reset');
  }

  adminAction(action: string, metadata?: Record<string, any>) {
    this.info(`Admin action: ${action}`, {
      action,
      isAdmin: true,
      ...metadata,
    });
  }

  apiError(endpoint: string, error: Error) {
    this.error(`API error: ${endpoint}`, error, {
      endpoint,
    });
  }

  validationError(field: string, value: any) {
    this.warn('Validation error', {
      field,
      value,
    });
  }

  // Navigation and UI events
  tabChanged(fromTab: string, toTab: string) {
    this.info('Tab changed', { fromTab, toTab });
  }

  pageView(page: string) {
    this.info('Page viewed', { page });
  }

  // Form interaction events
  fieldFocused(fieldName: string) {
    this.debug('Field focused', { fieldName });
  }

  fieldBlurred(fieldName: string, value: any) {
    this.debug('Field blurred', { fieldName, hasValue: !!value });
  }

  // Performance logging
  performanceMetric(metric: string, value: number, metadata?: Record<string, any>) {
    this.info('Performance metric', { metric, value, ...metadata });
  }

  // Feature usage
  featureUsed(feature: string, metadata?: Record<string, any>) {
    this.info('Feature used', { feature, ...metadata });
  }
}

export const logger = new Logger();
